import {
  GoogleAuthProvider,
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
} from "firebase/auth";

const REDIRECT_FALLBACK_CODES = new Set([
  "auth/popup-blocked",
  "auth/web-storage-unsupported",
  "auth/operation-not-supported-in-this-environment",
]);

const USER_CANCELLED_CODES = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
]);

const FRIENDLY_MESSAGES = {
  "auth/network-request-failed": "We couldn't reach Google. Check your connection and try again.",
  "auth/too-many-requests": "Too many sign-in attempts. Wait a minute, then try again.",
  "auth/user-disabled": "This account has been disabled. Contact support if you think this is a mistake.",
  "auth/unauthorized-domain": "Sign-in isn't enabled on this address. Open bandzjournal.vercel.app and try again.",
  "auth/account-exists-with-different-credential": "An account already exists with this email using a different sign-in method.",
  "auth/internal-error": "Google sign-in hit a temporary problem. Please try again.",
};

function normalizeCode(error) {
  return String(error?.code || "").toLowerCase().trim();
}

/** True when the person closed the popup themselves; callers should stay silent. */
export function isGoogleSignInCancelled(error) {
  return USER_CANCELLED_CODES.has(normalizeCode(error));
}

export function getGoogleSignInErrorMessage(error) {
  const code = normalizeCode(error);

  if (USER_CANCELLED_CODES.has(code)) {
    return "Google sign-in was closed before it finished.";
  }

  if (REDIRECT_FALLBACK_CODES.has(code)) {
    return "Popup sign-in is not available here, so we are redirecting you to Google instead.";
  }

  return FRIENDLY_MESSAGES[code] || "Google sign-in didn't finish. Please try again.";
}

export async function signInWithGoogle(auth) {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });

  try {
    await signInWithPopup(auth, provider);
    return { mode: "popup" };
  } catch (error) {
    const code = normalizeCode(error);

    if (REDIRECT_FALLBACK_CODES.has(code)) {
      await signInWithRedirect(auth, provider);
      return {
        mode: "redirect",
        message: getGoogleSignInErrorMessage(error),
      };
    }

    throw error;
  }
}

/**
 * After a redirect sign-in returns to the app, resolves with the error that
 * ended it (or null). Successful sign-ins are picked up by the auth listener.
 */
export async function getGoogleRedirectError(auth) {
  try {
    await getRedirectResult(auth);
    return null;
  } catch (error) {
    return isGoogleSignInCancelled(error) ? null : error;
  }
}
